// HistoryViewer.tsx
import React, { useState, useMemo } from 'react';
import ToastManager from '../../../Components/ToastManager';

interface HistoryViewerProps {
    historyData: any[];
    onClose: () => void;
}

interface VersionItem {
    id: string;
    traceId: number;
    version: number;
    label: string;
    data: any;
    creationDate: string;
    lastUpdatedBy?: string;
    comments: string;
    selected: boolean;
    originalIndex: number;
}

interface DiffResult {
    path: string;
    type: 'added' | 'removed' | 'changed';
    leftValue?: any;
    rightValue?: any;
}

// Custom diff function to replace deep-diff
const getDiff = (left: any, right: any, path: string = ''): DiffResult[] => {
    const diffs: DiffResult[] = [];

    // Handle primitive types
    if (typeof left !== 'object' || left === null || typeof right !== 'object' || right === null) {
        if (left !== right) {
            diffs.push({
                path: path || 'root',
                type: 'changed',
                leftValue: left,
                rightValue: right
            });
        }
        return diffs;
    }

    // Handle arrays
    if (Array.isArray(left) || Array.isArray(right)) {
        const leftArray = Array.isArray(left) ? left : [];
        const rightArray = Array.isArray(right) ? right : [];
        const maxLen = Math.max(leftArray.length, rightArray.length);

        for (let i = 0; i < maxLen; i++) {
            const currentPath = path ? `${path}[${i}]` : `[${i}]`;
            if (i >= leftArray.length) {
                diffs.push({
                    path: currentPath,
                    type: 'added',
                    rightValue: rightArray[i]
                });
            } else if (i >= rightArray.length) {
                diffs.push({
                    path: currentPath,
                    type: 'removed',
                    leftValue: leftArray[i]
                });
            } else {
                diffs.push(...getDiff(leftArray[i], rightArray[i], currentPath));
            }
        }
        return diffs;
    }

    // Handle objects
    const allKeys: any = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
    
    for (const key of allKeys) {
        const currentPath = path ? `${path}.${key}` : key;
        const leftValue = left?.[key];
        const rightValue = right?.[key];

        if (!(key in left)) {
            diffs.push({
                path: currentPath,
                type: 'added',
                rightValue: rightValue
            });
        } else if (!(key in right)) {
            diffs.push({
                path: currentPath,
                type: 'removed',
                leftValue: leftValue
            });
        } else {
            diffs.push(...getDiff(leftValue, rightValue, currentPath));
        }
    }

    return diffs;
};

const HistoryViewer: React.FC<HistoryViewerProps> = ({ historyData, onClose }) => {
    const [versions, setVersions] = useState<VersionItem[]>([]);

    // Process history data into versions
    useMemo(() => {
        if (!historyData || historyData.length === 0) {
            setVersions([]);
            return;
        }

        const processedVersions: VersionItem[] = [];
        
        // Sort by traceId ascending (oldest first)
        const sortedData = [...historyData].sort((a, b) => a.traceId - b.traceId);
        
        let versionCounter = 0;
        
        // Process each history entry in order (oldest to newest)
        for (let i = 0; i < sortedData.length; i++) {
            const item = sortedData[i];
            
            // For the first entry (oldest traceId)
            // payload = initial state (Version 0)
            // newPayload = first change (Version 1)
            if (i === 0) {
                // Add payload as first version (initial state)
                if (item.payload && Object.keys(item.payload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-payload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.payload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.createdBy || item.lastUpdatedBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
                
                // Add newPayload as next version
                if (item.newPayload && Object.keys(item.newPayload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-newPayload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.newPayload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.lastUpdatedBy || item.createdBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
            } else {
                // For subsequent entries, only add newPayload
                // (payload equals previous newPayload, so it's already added)
                if (item.newPayload && Object.keys(item.newPayload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-newPayload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.newPayload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.lastUpdatedBy || item.createdBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
            }
        }

        // If no versions were created from payloads, create versions from newPayload only
        if (processedVersions.length === 0) {
            for (let i = 0; i < sortedData.length; i++) {
                const item = sortedData[i];
                if (item.newPayload && Object.keys(item.newPayload).length > 0) {
                    processedVersions.push({
                        id: `${item.traceId}-newPayload-${versionCounter}`,
                        traceId: item.traceId,
                        version: versionCounter,
                        label: `Version ${versionCounter}`,
                        data: item.newPayload,
                        creationDate: item.creationDate,
                        lastUpdatedBy: item.lastUpdatedBy || item.createdBy,
                        comments: item.comments || '',
                        selected: false,
                        originalIndex: i
                    });
                    versionCounter++;
                }
            }
        }

        // Now processedVersions is in ascending order (oldest to newest)
        // Version numbers should be: oldest = 0, newest = length - 1
        
        // Keep them in ascending order (oldest first, newest last)
        // This means Version 0 is the oldest, Version N is the latest
        processedVersions.forEach((v, idx) => {
            v.version = idx;
            v.label = `Version ${idx}`;
        });

        // Mark the last version as latest BEFORE reversing
        if (processedVersions.length > 0) {
            const lastIndex = processedVersions.length - 1;
            processedVersions[lastIndex] = {
                ...processedVersions[lastIndex],
                label: `Version ${lastIndex}`
            };
        }

        // Reverse to show latest first (Version N on top, Version 0 at bottom)
        const reversedVersions = [...processedVersions].reverse();
        setVersions(reversedVersions);
        
        // Auto-select the last two versions (which are now the first two in reversed list)
        if (reversedVersions.length >= 2) {
            const updated = reversedVersions.map((v, idx) => ({
                ...v,
                selected: idx === 0 || idx === 1
            }));
            setVersions(updated);
        } else if (reversedVersions.length === 1) {
            const updated = reversedVersions.map((v) => ({
                ...v,
                selected: true
            }));
            setVersions(updated);
        }
    }, [historyData]);

    const handleVersionSelection = (versionId: string) => {
        const currentSelected = versions.filter(v => v.selected);
        const version = versions.find(v => v.id === versionId);
        
        if (!version) return;

        // If clicking on already selected version, deselect it
        if (version.selected) {
            const updatedVersions = versions.map(v => 
                v.id === versionId ? { ...v, selected: false } : v
            );
            setVersions(updatedVersions);
            return;
        }

        // Check if we already have 2 selected
        if (currentSelected.length >= 2) {
            ToastManager.showToast("Only 2 versions can be compared at once", 'Warning');
            return;
        }

        // Select the version
        const updatedVersions = versions.map(v => 
            v.id === versionId ? { ...v, selected: true } : v
        );
        setVersions(updatedVersions);
    };

    const handleClearAll = () => {
        const updatedVersions = versions.map(v => ({
            ...v,
            selected: false
        }));
        setVersions(updatedVersions);
    };

    const getSelectedItems = () => {
        return versions.filter(v => v.selected);
    };

    const getJsonLinesWithPaths = (obj: any, path: string = '', indent: number = 0): { text: string, path: string }[] => {
        const spacing = "  ".repeat(indent);
        if (obj === null) return [{ text: `${spacing}null`, path }];

        if (Array.isArray(obj)) {
            let lines: { text: string, path: string }[] = [{ text: `${spacing}[`, path }];
            obj.forEach((item, i) => {
                const itemPath = `${path}[${i}]`;
                const itemLines = getJsonLinesWithPaths(item, itemPath, indent + 1);
                if (i < obj.length - 1) itemLines[itemLines.length - 1].text += ",";
                lines = lines.concat(itemLines);
            });
            lines.push({ text: `${spacing}]`, path });
            return lines;
        }

        if (typeof obj === 'object') {
            let lines: { text: string, path: string }[] = [{ text: `${spacing}{`, path }];
            const keys = Object.keys(obj);
            keys.forEach((key, i) => {
                const keyPath = path ? `${path}.${key}` : key;
                const value = obj[key];
                const prefix = `${spacing}  "${key}": `;

                if (value !== null && typeof value === 'object') {
                    const startChar = Array.isArray(value) ? "[" : "{";
                    lines.push({ text: prefix + startChar, path: keyPath });
                    const valueLines = getJsonLinesWithPaths(value, keyPath, indent + 1);
                    valueLines.shift(); // remove the first line which is just [ or {
                    if (i < keys.length - 1) valueLines[valueLines.length - 1].text += ",";
                    lines = lines.concat(valueLines);
                } else {
                    lines.push({ text: `${prefix}${JSON.stringify(value)}${i < keys.length - 1 ? "," : ""}`, path: keyPath });
                }
            });
            lines.push({ text: `${spacing}}`, path });
            return lines;
        }

        return [{ text: `${spacing}${JSON.stringify(obj)}`, path }];
    };

    const renderInlineHighlightedJSON = (data: any, diffs: DiffResult[], isLeft: boolean) => {
        if (!data) return <pre className="p-2 mb-0" style={{ fontSize: '12px' }}>null</pre>;

        const linesWithPaths = getJsonLinesWithPaths(data);

        if (diffs.length === 0) {
            return (
                <div className="p-2 mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {linesWithPaths.map((lp, index) => (
                        <div key={index} style={{ whiteSpace: 'pre' }}>{lp.text}</div>
                    ))}
                </div>
            );
        }

        return (
            <div className="p-2 mb-0" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {linesWithPaths.map((lp, index) => {
                    // Find if this line's path or any of its parent paths are in diffs
                    const diff = diffs.find(d =>
                        d.path === lp.path ||
                        (lp.path && (lp.path.startsWith(d.path + '.') || lp.path.startsWith(d.path + '['))) ||
                        (d.path === 'root' && lp.path === '')
                    );

                    if (diff) {
                        let bgColor = 'bg-warning bg-opacity-25';
                        let borderColor = 'border-warning';
                        const type = diff.type;

                        if (type === 'added') {
                            bgColor = 'bg-success bg-opacity-25';
                            borderColor = 'border-success';
                        } else if (type === 'removed') {
                            bgColor = 'bg-danger bg-opacity-25';
                            borderColor = 'border-danger';
                        }

                        return (
                            <div
                                key={index}
                                className={`${bgColor} p-1 my-0 ${borderColor} border-start border-3`}
                                title={`Diff: ${diff.path} (${type})`}
                            >
                                <span style={{ whiteSpace: 'pre' }}>{lp.text}</span>
                            </div>
                        );
                    }
                    return <div key={index} style={{ whiteSpace: 'pre' }}>{lp.text}</div>;
                })}
            </div>
        );
    };

    const renderSideBySideComparison = (version1: VersionItem, version2: VersionItem) => {
        const diffs = getDiff(version1.data, version2.data);
        const hasDifferences = diffs.length > 0;

        return (
            <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className="d-flex justify-content-between align-items-center p-2 px-3 border-bottom bg-white">
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center text-muted border-end pe-3">
                            <i className="fa-solid fa-code-compare me-2"></i>
                            <span className="small text-uppercase fw-bold tracking-wider" style={{ letterSpacing: '0.05rem' }}>Diff Analysis</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10 rounded-pill">{version1.label}</span>
                            <i className="fa-solid fa-arrow-right-long text-muted small px-1"></i>
                            <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-10 rounded-pill">{version2.label}</span>
                        </div>
                    </div>
                    <div>
                        {!hasDifferences ? (
                            <span className="text-success small fw-bold">
                                <i className="fa-solid fa-circle-check me-1"></i>
                                Identical
                            </span>
                        ) : (
                            <span className="text-warning-emphasis small fw-bold bg-warning bg-opacity-10 px-2 py-1 rounded border border-warning border-opacity-25">
                                <i className="fa-solid fa-circle-info me-1"></i>
                                {diffs.length} Change{diffs.length !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                </div>
                {hasDifferences ? (
                    <div style={{ flex: 1, overflow: 'auto', display: 'flex' }} className="bg-light">
                        <div style={{ flex: 1, overflow: 'auto', borderRight: '1px solid #eee' }} className="bg-white">
                            <div className="bg-white p-2 border-bottom sticky-top d-flex justify-content-between align-items-center">
                                <span className="small fw-bold text-success opacity-75">
                                    <i className="fa-solid fa-clock-rotate-left me-1"></i> ORIGINAL
                                </span>
                                <span className="text-muted" style={{ fontSize: '0.7rem' }}>{version2.creationDate}</span>
                            </div>
                            <div className="p-1">
                                {renderInlineHighlightedJSON(version2.data, diffs, false)}
                            </div>
                        </div>
                        <div style={{ flex: 1, overflow: 'auto' }} className="bg-white">
                            <div className="bg-white p-2 border-bottom sticky-top d-flex justify-content-between align-items-center">
                                <span className="small fw-bold text-primary opacity-75">
                                    <i className="fa-solid fa-magic-wand-sparkles me-1"></i> MODIFIED
                                </span>
                                <span className="text-muted" style={{ fontSize: '0.7rem' }}>{version1.creationDate}</span>
                            </div>
                            <div className="p-1">
                                {renderInlineHighlightedJSON(version1.data, diffs, true)}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center p-5 bg-white h-100 d-flex flex-column justify-content-center">
                        <div className="mb-3 opacity-25">
                            <i className="fa-regular fa-check-circle fa-4x text-success"></i>
                        </div>
                        <h5 className="fw-bold text-dark">No Differences</h5>
                        <p className="text-muted small">The contents match exactly.</p>
                    </div>
                )}
            </div>
        );
    };

    const renderComparison = () => {
        const selectedItems = getSelectedItems();

        if (selectedItems.length === 0) {
            return (
                <div className="text-center p-5 text-muted bg-white h-100 d-flex flex-column justify-content-center">
                    <div className="mb-3 opacity-10">
                        <i className="fa-solid fa-layer-group fa-4x"></i>
                    </div>
                    <h6 className="fw-bold">No Version Selected</h6>
                    <p className="small">Select one or two versions above to begin.</p>
                </div>
            );
        }

        if (selectedItems.length === 1) {
            const version = selectedItems[0];
            return (
                <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }} className="bg-white">
                    <div className="bg-white p-2 px-3 border-bottom d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                            <i className="fa-solid fa-file-code text-primary opacity-50"></i>
                            <span className="small fw-bold text-dark text-uppercase tracking-wider" style={{ letterSpacing: '0.05rem' }}>{version.label} Preview</span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                <i className="fa-regular fa-calendar-check me-1"></i>
                                {version.creationDate}
                            </span>
                            <span className="text-muted border-start ps-3" style={{ fontSize: '0.75rem' }}>
                                <i className="fa-solid fa-circle-user me-1"></i>
                                {version.lastUpdatedBy || 'System'}
                            </span>
                        </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto' }} className="p-1">
                        {renderInlineHighlightedJSON(version.data, [], false)}
                    </div>
                </div>
            );
        }

        const [version1, version2] = selectedItems;
        return renderSideBySideComparison(version1, version2);
    };

    if (!versions || versions.length === 0) {
        return <div className="text-center p-5 font-italic">No History Found</div>;
    }

    const selectedCount = versions.filter(v => v.selected).length;

    return (
        <div style={{ height: '90vh', display: 'flex', flexDirection: 'row' }} className="history-viewer-container bg-white border rounded shadow-sm overflow-hidden">
            {/* Left Sidebar: Version List */}
            <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column' }} className="border-end bg-light bg-opacity-25">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white">
                    <div className="d-flex align-items-center gap-2">
                        <i className="fa-solid fa-list-ul text-secondary small"></i>
                        <span className="fw-bold small text-uppercase" style={{ letterSpacing: '0.05rem' }}>History</span>
                    </div>
                    {selectedCount > 0 && (
                        <button className="btn btn-link btn-sm p-0 text-decoration-none text-primary small fw-bold" onClick={handleClearAll} style={{ fontSize: '0.75rem' }}>
                            Reset
                        </button>
                    )}
                </div>
                
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }} className="custom-scrollbar">
                    {versions.map((version, index) => {
                        const isSelected = version.selected;
                        const isDisabled = !isSelected && selectedCount >= 2;
                        const isLatest = index === 0 && version.version > 0;

                        return (
                            <div 
                                key={version.id}
                                className={`p-3 border rounded-3 mb-3 transition-all cursor-pointer ${
                                    isSelected 
                                    ? 'border-info shadow-sm bg-info bg-opacity-10' 
                                    : 'border-light-subtle bg-white hover-shadow'
                                }`}
                                style={{ 
                                    opacity: isDisabled ? 0.5 : 1,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    borderWidth: isSelected ? '2px' : '1px'
                                }}
                                onClick={() => !isDisabled && handleVersionSelection(version.id)}
                            >
                                <div className="d-flex align-items-start gap-3">
                                    <div className="form-check pt-0 mt-1">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input shadow-none"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleVersionSelection(version.id);
                                            }}
                                            disabled={isDisabled}
                                        />
                                    </div>
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span className={`fw-bold ${isSelected ? 'text-info-emphasis' : 'text-dark'}`} style={{ fontSize: '0.9rem' }}>
                                                {version.label}
                                            </span>
                                            {isLatest && (
                                               <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-10" style={{ fontSize: '0.6rem' }}>Latest</span>
                                            )}
                                        </div>
                                        <div className="text-muted d-flex align-items-center mb-1" style={{ fontSize: '0.75rem' }}>
                                            <i className="fa-regular fa-clock me-1"></i>
                                            {version.creationDate}
                                        </div>
                                        <div className="text-muted small text-truncate" style={{ fontSize: '0.75rem' }} title={version.comments}>
                                            <i className="fa-regular fa-user me-1"></i>
                                            {version.lastUpdatedBy || 'System'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {selectedCount > 0 && (
                    <div className="p-3 bg-white border-top">
                        <div className="d-grid">
                            <div className="small text-muted text-center mb-0">
                                {selectedCount === 1 ? 'Showing 1 version' : 'Comparing 2 versions'}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Right Side: content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} className="bg-white">
                <style>
                    {`
                        .hover-shadow:hover {
                            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                            border-color: #dee2e6 !important;
                        }
                        .form-check-input:checked {
                            background-color: #0dcaf0;
                            border-color: #0dcaf0;
                        }
                    `}
                </style>
                <div style={{ flex: 1, overflow: 'auto' }}>
                    {renderComparison()}
                </div>
            </div>
        </div>
    );
};

export default HistoryViewer;